import Papa from 'papaparse';
import {
  LineType,
  type DocumentLine,
} from '@paeffgen/react/crm-documents/data-access-graphql';

/**
 * Result of parsing a CSV file into document lines.
 * Lines that could not be matched to a known article are flagged with `isUnknown`.
 */
export interface CsvImportLine extends DocumentLine {
  isUnknown: boolean;
}

/**
 * Callback used to resolve article data from an article number.
 * The caller must provide this — it may query Apollo cache, a local list, etc.
 */
export interface ArticleLookup {
  (articleNumber: string): {
    name: string;
    unit: string;
    basePrice: number;
  } | null;
}

/**
 * Parse a CSV file (two columns: ArticleNumber, Quantity) into DocumentLine[].
 *
 * The lookup function resolves article metadata. Articles not found are
 * still returned but marked `isUnknown: true`.
 */
export async function parseCsvToDocumentLines(
  file: File,
  lookupArticle: ArticleLookup
): Promise<CsvImportLine[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      complete: (results) => {
        try {
          const rows = results.data as string[][];

          // Skip header row if present
          const startRow =
            rows[0] &&
            (String(rows[0][0]) === 'ArticleNumber' ||
              String(rows[0][0]) === 'Artikelnummer')
              ? 1
              : 0;

          const lines: CsvImportLine[] = rows
            .slice(startRow)
            .map((row, index): CsvImportLine | null => {
              const articleNumber = row[0] ? String(row[0]).trim() : '';
              const quantity = row[1] ? parseFloat(String(row[1])) : 1;
              if (!articleNumber) return null;

              const found = lookupArticle(articleNumber);
              const isUnknown = !found;
              const qty = isNaN(quantity) ? 1 : quantity;

              return {
                lineNumber: ((index + 1) * 10).toString(),
                lineType: LineType.ARTICLE,
                articleNumber,
                description: found
                  ? found.name
                  : `Unbekannter Artikel: ${articleNumber}`,
                description2: null,
                quantity: qty,
                packageQuantity: null,
                quantityPerPackage: null,
                piecesPerPackage: null,
                unit: found ? found.unit : 'Stk',
                unitPrice: found ? found.basePrice : 0,
                netUnitPrice: null,
                discount: null,
                amount: found ? found.basePrice * qty : 0,
                isReturn: false,
                priceFactor: 1,
                deliveryNumber: null,
                orderNumber: null,
                isUnknown,
              };
            })
            .filter((l): l is CsvImportLine => l !== null);

          resolve(lines);
        } catch (err) {
          reject(err);
        }
      },
      error: (error) => {
        reject(error);
      },
    });
  });
}
